import React from "react";
import Button from "@mui/material/Button";

/**
 * StyledCancelButton Props
 * ------------------------
 * onClick       - Click callback function
 * children      - Button label or elements inside the button
 * isMobile      - Optional flag to adjust button size & padding for mobile view
 * minWidthAuto  - Optional flag to disable MUI's minWidth behavior
 */
type Props = {
    onClick: () => void;
    children: React.ReactNode;
    isMobile?: boolean;
    minWidthAuto?: boolean;
};

/**
 * StyledCancelButton Component
 * ----------------------------
 * A customizable cancel-style button designed using Material UI's Button component.
 * 
 * Features:
 * - Fully responsive (mobile vs desktop sizing)
 * - Custom styling applied via MUI `sx`
 * - Strong override using "!important" to force exact UI consistency
 * - Custom hover style and brand color system
 */
const StyledCancelButton: React.FC<Props> = ({
    onClick,
    children,
    isMobile,
    minWidthAuto,
}) => (
    <Button
        variant="outlined"
        color="inherit"
        onClick={onClick}
        sx={{
            marginTop: 12,

            /* Button width: 100% on mobile, 10% on desktop */
            width: isMobile ? "100% !important" : "10% !important",

            /* Handle min-width behavior */
            minWidth: minWidthAuto
                ? "none !important"
                : isMobile
                ? "120px !important"
                : "100px !important",

            borderRadius: "8px !important",
            margin: "0 auto !important",
            display: "block !important",

            /* Adjust padding based on device size */
            padding: isMobile
                ? "10px 0 !important"
                : "5px 0 !important",

            /* Font and color styling */
            color: "#fff !important",
            backgroundColor: "#f15b65ff !important",
            textTransform: "none !important",
            fontFamily: "var(--font-family-main) !important",

            /* Hover State */
            "&:hover": {
                backgroundColor: "#f4303dff !important",
            },
        }}
    >
        {children}
    </Button>
);

export default StyledCancelButton;
